/**
 * The permission catalogue — 29. shared-contract.md §2.
 *
 * The specification disagrees with itself three ways (§7.11 says 29, its own
 * figure caption says 27, the §6.4 code block lists 31). The disagreement
 * resolves against the endpoint catalogue rather than by opinion: neither
 * `devices.command` nor `config.read` has any route in §6.12, which is exactly
 * the ground §7.11 gives for removing them. 31 − 2 = 29, and the "27" is a
 * stale figure caption.
 *
 * Drive the matrix screen from this data. Hard-code no count anywhere; the
 * length is asserted in `permissions.test.ts`.
 */
export const PERMISSIONS = [
  'bays.read',
  'bays.write',
  'bays.override_state',
  'zones.read',
  'zones.write',
  'devices.read',
  'devices.map',
  'calibration.read',
  'calibration.write',
  'signs.read',
  'signs.write',
  'alerts.read',
  'alerts.acknowledge',
  'alerts.configure',
  'reports.read',
  'reports.schedule',
  'exports.create',
  'exports.download',
  'valet.log',
  'valet.search',
  'valet.correct',
  'health.read',
  'health.diagnose',
  'maintenance.write',
  'users.read',
  'users.manage',
  'roles.manage',
  'integrations.manage',
  'audit.read',
] as const;

export type Permission = (typeof PERMISSIONS)[number];

/** The ten roles. Nothing in the code ever checks a role *name* — §6.1. */
export const ROLES = [
  'system_admin',
  'udc_management',
  'retail_property_mgmt',
  'valet_supervisor',
  'valet_staff',
  'security',
  'fm_maintenance',
  'udc_it',
  'enforcement',
  'commissioning',
] as const;

export type RoleCode = (typeof ROLES)[number];

export const ROLE_LABELS: Readonly<Record<RoleCode, string>> = {
  system_admin: 'System administrator',
  udc_management: 'UDC management',
  retail_property_mgmt: 'Retail property management',
  valet_supervisor: 'Valet supervisor',
  valet_staff: 'Valet staff',
  security: 'Security',
  fm_maintenance: 'FM / maintenance',
  udc_it: 'UDC IT',
  enforcement: 'Enforcement',
  commissioning: 'Commissioning',
};

export const PERMISSION_LABELS: Readonly<Record<Permission, string>> = {
  'bays.read': 'Read bays',
  'bays.write': 'Write bays',
  'bays.override_state': 'Override bay state',
  'zones.read': 'Read zones',
  'zones.write': 'Write zones',
  'devices.read': 'Read devices',
  'devices.map': 'Map devices to bays',
  'calibration.read': 'Read calibration',
  'calibration.write': 'Write calibration',
  'signs.read': 'Read signs',
  'signs.write': 'Write signs',
  'alerts.read': 'Read alerts',
  'alerts.acknowledge': 'Acknowledge and resolve alerts',
  'alerts.configure': 'Configure alert rules',
  'reports.read': 'Run reports',
  'reports.schedule': 'Schedule reports',
  'exports.create': 'Create dataset exports',
  'exports.download': 'Download generated documents',
  'valet.log': 'Log valet sessions',
  'valet.search': 'Search valet sessions',
  'valet.correct': 'Correct valet sessions',
  'health.read': 'Read system health',
  'health.diagnose': 'Diagnose the pipeline',
  'maintenance.write': 'Raise maintenance work',
  'users.read': 'Read users',
  'users.manage': 'Manage users and zone scope',
  'roles.manage': 'Manage roles and permissions',
  'integrations.manage': 'Manage API keys and webhooks',
  'audit.read': 'Read the audit log',
};

/**
 * A write permission is incoherent without its matching read, and the server
 * refuses such a role (W-6.4). One table, used by the mock handler and by the
 * custom-role editor, so the two cannot disagree.
 */
export const WRITE_REQUIRES_READ: Readonly<Partial<Record<Permission, Permission>>> = {
  'bays.write': 'bays.read',
  'bays.override_state': 'bays.read',
  'zones.write': 'zones.read',
  'devices.map': 'devices.read',
  'calibration.write': 'calibration.read',
  'signs.write': 'signs.read',
  'alerts.acknowledge': 'alerts.read',
  'alerts.configure': 'alerts.read',
  'reports.schedule': 'reports.read',
  'exports.download': 'reports.read',
  'valet.correct': 'valet.search',
  'health.diagnose': 'health.read',
  'users.manage': 'users.read',
  'roles.manage': 'users.read',
};

export function isPermission(value: string): value is Permission {
  return (PERMISSIONS as readonly string[]).includes(value);
}

/** Returns the reads a permission set is missing, or an empty array. */
export function missingReadPermissions(granted: readonly Permission[]): Permission[] {
  const held = new Set(granted);
  const missing = new Set<Permission>();
  for (const permission of granted) {
    const required = WRITE_REQUIRES_READ[permission];
    if (required && !held.has(required)) missing.add(required);
  }
  return [...missing];
}
